import { gql, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import { parse, stringify } from 'query-string';
import React, { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { Route } from 'react-router-dom';
import { Routes } from 'react-router-dom-v5-compat';

import { useIsLoggedIn } from 'src/app/graph/hooks/useIsLoggedIn';
import { trackUserId } from 'src/lib/analytics';
import {
  getRedactedHref,
  getRedactedReferrer,
} from 'src/lib/analytics/redactSensitiveUrl';
import {
  scrubSentryBreadcrumb,
  scrubSentryEvent,
} from 'src/lib/analytics/scrubSentryEvent';
import Config from 'src/lib/config';
import {
  type Consent,
  FULL_CONSENT,
  enforceGpcInOneTrust,
  loadOneTrust,
  onConsentChange,
  parseConsent,
  purgeDisallowed,
} from 'src/lib/consent/consent';
import {
  type TrackingScripts,
  getTrackingScripts,
} from 'src/lib/consent/trackingScripts';
import type { GraphQLTypes } from 'src/lib/graphqlTypes';

import { useQueryParam } from './useQueryParam';

export const SentryRoute = Sentry.withSentryRouting(Route);
export const SentryRoutes = Sentry.withSentryRouting(Routes);
// Counsel (2026-10-29): Sentry may run before cookie consent as error
// reporting only, so it has no performance tracing, no session tracking, and
// no URL parameters or console output in what it sends.
export const initSentry = () => {
  Sentry.init({
    dsn: 'https://4f2fd0298c0044f5907713d5766d144a@sentry.io/283526',
    environment: Config.settings.env,
    // no session tracking, and no console breadcrumbs, which can hold
    // GraphQL operations
    integrations: (defaults) => [
      ...defaults.filter(
        ({ name }) => name !== 'BrowserSession' && name !== 'Breadcrumbs',
      ),
      Sentry.breadcrumbsIntegration({ console: false }),
    ],
    beforeSend: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryBreadcrumb,
    attachStacktrace: true,
    enabled: Config.sentryEnabled,
    ...(Config.gitCommitHash && { release: Config.gitCommitHash }),
  });
};

export const useTelemetryInitializer = (isEmbedded: boolean) => {
  const runTelemetry = useQueryParam('runTelemetry');

  const { data, loading } = useQuery(gql<
    GraphQLTypes.RoutedAppMeQuery,
    GraphQLTypes.RoutedAppMeQueryVariables
  >/* GraphQL */ `
    query RoutedAppMeQuery {
      me {
        id
      }
    }
  `);

  const { isLoggedIn, loading: loadingIsLoggedIn } = useIsLoggedIn();

  React.useEffect(() => {
    // on the embeddable Explorer, we default to opting out of telemetry
    // because dev tools can't run telemetry for legal reasons, and we
    // need all previous versions of dev tools to opt out
    // 02/08/2023 - don't run telemetry until loading is false
    // The host page can't consent on behalf of its logged out visitors, so
    // `runTelemetry=true` only applies to logged in users.
    if (
      !loading &&
      !loadingIsLoggedIn &&
      (!isEmbedded || (isLoggedIn && runTelemetry === 'true'))
    ) {
      initializeTelemetryDependencies(isLoggedIn);
      if (isLoggedIn) Sentry.setUser({ id: data?.me?.id });
    }
  }, [
    data?.me?.id,
    isEmbedded,
    isLoggedIn,
    loading,
    loadingIsLoggedIn,
    runTelemetry,
  ]);

  useEffect(() => {
    if (!loading && !loadingIsLoggedIn) {
      // The Google Analytics User ID policy requires that you stop any measurement
      // based on the user ID once the user signs out of your website.
      // https://support.google.com/tagmanager/answer/4565987
      trackUserId(isLoggedIn ? data?.me?.id : undefined);
    }
  }, [isLoggedIn, loadingIsLoggedIn, data?.me?.id, loading]);
};

let hasInitialized = false;
const initializeTelemetryDependencies = (isLoggedIn: boolean) => {
  if (hasInitialized) return;
  hasInitialized = true;

  // only inject OneTrust for logged out users, and not for local dev
  if (isLoggedIn || window.location.hostname === 'localhost') {
    initTrackingScripts(getTrackingScripts(true, FULL_CONSENT));
    return;
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  // Google Consent Mode v2. This must be queued before anything else reaches
  // the dataLayer, including OneTrust, which defines its own `window.gtag`.
  pushGtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  /* eslint-enable @typescript-eslint/naming-convention */

  const isSandbox = window.location.pathname.includes('sandbox');

  // Logged out visitors load nothing until they opt in through OneTrust, which
  // fires this once when it loads and again on every choice.
  let granted: Consent = { performance: false, targeting: false };
  onConsentChange(() => {
    enforceGpcInOneTrust(navigator.globalPrivacyControl);
    const consent = parseConsent(
      window.OnetrustActiveGroups,
      navigator.globalPrivacyControl,
    );
    purgeDisallowed(consent);
    // scripts we've already loaded can't be unloaded
    if (
      (granted.performance && !consent.performance) ||
      (granted.targeting && !consent.targeting)
    ) {
      window.location.reload();
      return;
    }
    if (!consent.performance && !consent.targeting) return;

    const ads = consent.targeting ? 'granted' : 'denied';
    /* eslint-disable @typescript-eslint/naming-convention */
    pushGtag('consent', 'update', {
      analytics_storage: consent.performance ? 'granted' : 'denied',
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
    });
    /* eslint-enable @typescript-eslint/naming-convention */

    granted = consent;
    initTrackingScripts(getTrackingScripts(false, consent, isSandbox));
  });
  loadOneTrust();
};

// gtag.js only accepts commands queued as `arguments` objects
const pushGtag: Gtag.Gtag = function gtag() {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
};

// `window.gtag` may already be OneTrust's, so track our own
let hasDefinedGtag = false;
const defineGtag = () => {
  if (hasDefinedGtag) return;
  hasDefinedGtag = true;
  window.gtag = pushGtag;
  gtag('js', new Date());
};

// see https://developers.google.com/analytics/devguides/collection/gtagjs/sending-data#handle_timeouts
function createFunctionWithTimeout(callback: Function, optTimeout?: number) {
  let called = false;
  function fn() {
    if (!called) {
      called = true;
      callback();
    }
  }
  setTimeout(fn, optTimeout || 1000);
  return fn;
}

// consent can grow during a page view, so each script is loaded at most once
const loadedScripts = new Set<string>();

function initTrackingScripts({ gtmContainerId, ga4Ids }: TrackingScripts) {
  defineGtag();

  /* load Google Tag Manager */
  if (gtmContainerId && !loadedScripts.has(gtmContainerId)) {
    loadedScripts.add(gtmContainerId);
    TagManager.initialize({
      gtmId: gtmContainerId,
    });
  }

  /** load google analytics */
  const newGa4Ids = ga4Ids.filter((id) => !loadedScripts.has(id));
  if (newGa4Ids.length) {
    newGa4Ids.forEach((id) => {
      loadedScripts.add(id);
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      document.head.appendChild(script);
    });
    initGa4(newGa4Ids);
  }
}

function initGa4(ga4Ids: string[]) {
  // GA4 defaults `page_location`/`page_referrer` to the raw `window.location.href`
  // and `document.referrer`, which leaks password reset and invitation tokens to
  // `region1.analytics.google.com` (in the `dl`/`dr`/`ep.form_destination` params).
  // Overriding them at the config level applies to every event on the page,
  // including ones fired by the GTM container. See OE-1393.
  /* eslint-disable @typescript-eslint/naming-convention */
  const redactedPageParams = {
    page_location: getRedactedHref(),
    page_referrer: getRedactedReferrer(),
  };

  // disable default page_view event from firing so we can send it ourselves
  // to make use of the event callback and wait until ga has had a chance to load
  // before removing utms from the url
  ga4Ids.forEach((id) =>
    gtag('config', id, {
      ...redactedPageParams,
      send_page_view: false,
    }),
  );
  gtag('event', 'page_view', {
    ...redactedPageParams,
    send_to: ga4Ids,
    event_callback: () => {
      // remove utm params after analytics has fired the page_view event
      // wrapped in a timeout to ensure utms are still pulled in the event
      // ga doesn't load or this callback fails
      createFunctionWithTimeout(() => {
        // only do this if we have query params present
        if (!window.location.search) return;
        window.history.replaceState(
          {},
          document.title,
          `?${stringify(
            _.omit(parse(window.location.search), [
              Config.queryParameters.Referrer,
              Config.queryParameters.Campaign,
              Config.queryParameters.Medium,
              Config.queryParameters.Source,
            ]),
          )}${window.location.hash}`,
        );
      });
    },
  });
  /* eslint-enable @typescript-eslint/naming-convention */
}
